import { App, Form, Modal } from 'antd';
import { useEffect } from 'react';
import { useTranslation } from 'translations/hooks';

import { useGraphqlApiHeaders } from 'business/contextProviders/useApiHeaders';
import {
  GetSteeringDataQuery,
  Module_Enum,
  useUpdateParameterRingMutation,
} from 'generated/graphql';
import apolloClient from 'technical/graphql/client';
import { isUndefinedOrNull } from 'technical/utils/is-null-or-undefined';

import { UpdateSteeringFormValues } from './types';
import { UpdateRingForm } from './update-ring-form';

interface UpdateSteeringModalProps {
  isModalVisible: boolean;
  closeModal: () => void;
  ring: GetSteeringDataQuery['parameter_ring'][number] | null;
}

export const UpdateSteeringModal: React.FC<UpdateSteeringModalProps> = ({
  isModalVisible,
  closeModal,
  ring,
}) => {
  const { message } = App.useApp();

  const { t } = useTranslation();
  const [form] = Form.useForm<UpdateSteeringFormValues>();
  const headers = useGraphqlApiHeaders(Module_Enum.Steering);

  const [updateParameterRingMutation] = useUpdateParameterRingMutation({
    context: { headers },
  });

  const save = async (row: UpdateSteeringFormValues) => {
    await updateParameterRingMutation({
      variables: {
        id: ring?.id,
        updatedFields: {
          ...row,
        },
      },
      onCompleted: async () => {
        await apolloClient.reFetchObservableQueries();
        closeModal();
        message.open({
          type: 'success',
          content: t('productionAndPerformance.ring.edit.modal.update.success'),
        });
      },
      onError: () => {
        message.open({
          type: 'error',
          content: t('productionAndPerformance.ring.edit.modal.update.error'),
        });
      },
    });
  };

  useEffect(() => {
    if (ring) {
      form.setFieldsValue({
        ...ring,
      });
    }
  }, [form, ring]);

  return (
    <Modal
      title={t('productionAndPerformance.ring.edit.modal.title', {
        ringNumber: ring?.ringNumber,
      })}
      open={isModalVisible}
      onOk={form.submit}
      onCancel={closeModal}
      destroyOnHidden
    >
      {!isUndefinedOrNull(ring) ? (
        <UpdateRingForm form={form} save={save} />
      ) : null}
    </Modal>
  );
};
